// PaymentSuccess.jsx — FULL FIXED FILE
import React, { useEffect, useRef, useState } from 'react';
import { Download, CheckCircle, RefreshCw, Star, FileText, ExternalLink, Loader, AlertCircle } from 'lucide-react';

const Confetti = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const COLORS = ['#C8973A','#E8BF72','#1A1814','#4A5E52','#FAF8F5','#D4AF37'];
    const pieces = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * -canvas.height,
      r: Math.random() * 7 + 3, d: Math.random() * 60 + 20,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      tilt: Math.random() * 10 - 5, tiltAngle: 0,
      tiltSpeed: Math.random() * 0.1 + 0.05,
    }));
    let frame;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        ctx.beginPath(); ctx.lineWidth = p.r; ctx.strokeStyle = p.color;
        ctx.moveTo(p.x + p.tilt + p.r / 2, p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 2);
        ctx.stroke();
        p.tiltAngle += p.tiltSpeed;
        p.y += (Math.cos(p.d) + 2 + p.r / 2) * 0.8;
        p.tilt = 12 * Math.sin(p.tiltAngle);
        if (p.y > canvas.height) { p.y = -10; p.x = Math.random() * canvas.width; }
      });
      frame = requestAnimationFrame(draw);
    };
    draw();
    const timeout = setTimeout(() => cancelAnimationFrame(frame), 4000);
    return () => { cancelAnimationFrame(frame); clearTimeout(timeout); };
  }, []);
  return (
    <canvas ref={canvasRef} style={{
      position: 'absolute', inset: 0, width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 0,
    }} />
  );
};

const ScoreBadge = ({ score }) => {
  if (!score) return null;
  const color = score >= 80 ? '#4A8C5C' : score >= 55 ? '#C8973A' : '#C0392B';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 14px', borderRadius: 50,
      background: `${color}14`, border: `1px solid ${color}40`,
      fontSize: '0.78rem', fontWeight: 700, color, marginTop: 12,
    }}>
      <Star size={12} /> ATS Score: {score}/100
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const PaymentSuccess = ({
  downloadToken,
  latexSource,
  paymentId,
  atsScore = null,
  onReset,
}) => {
  const [dlState, setDlState] = useState('idle');
  const [dlError, setDlError] = useState('');
  // ── FIX 7: Track whether auto-download has been attempted
  // so it only fires once, not on every render
  const autoDownloadAttempted = useRef(false);

  const triggerDownload = async () => {
    // ── FIX 8: Guard against missing credentials — this was the
    // core crash: useEffect fired before props arrived
    if (!downloadToken || !latexSource) {
      setDlError('Missing download credentials. Please contact support.');
      setDlState('error');
      return;
    }

    setDlState('loading');
    setDlError('');

    try {
      const res = await fetch('/api/download-pdf', {
        method : 'POST',
        headers: {
          'Content-Type'    : 'application/json',
          'X-Download-Token': downloadToken,
        },
        body: JSON.stringify({ downloadToken, latexSource }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server error (${res.status})`);
      }

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'resume.pdf';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);

      setDlState('done');

    } catch (err) {
      setDlError(err.message || 'Download failed. Please try again.');
      setDlState('error');
    }
  };

  // ── FIX 9: Auto-trigger only fires ONCE and only when both
  // downloadToken and latexSource are actually present.
  // Previously: fired on mount with empty deps → props not ready yet
  useEffect(() => {
    if (downloadToken && latexSource && !autoDownloadAttempted.current) {
      autoDownloadAttempted.current = true;
      triggerDownload();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadToken, latexSource]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--rb-cream)', padding: '24px 16px',
      position: 'relative', overflow: 'hidden',
    }}>
      <Confetti />

      <div style={{
        position: 'relative', zIndex: 1, maxWidth: 480, width: '100%',
        background: 'var(--rb-card-bg)', border: '1px solid var(--rb-border)',
        borderRadius: 20, boxShadow: 'var(--rb-shadow-lg)', overflow: 'hidden',
      }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, var(--rb-gold), var(--rb-gold-lt))' }} />

        <div style={{ padding: '40px 36px 36px', textAlign: 'center' }}>

          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(74,140,92,0.10)', border: '2px solid rgba(74,140,92,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 22px',
          }}>
            <CheckCircle size={34} style={{ color: '#4A8C5C' }} />
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display', serif", fontSize: '1.7rem', fontWeight: 700,
            color: 'var(--rb-ink)', margin: '0 0 10px',
          }}>Payment Confirmed!</h1>

          <p style={{ fontSize: '0.88rem', color: 'var(--rb-muted)', lineHeight: 1.6, margin: '0 0 6px' }}>
            {dlState === 'loading' && 'Compiling your resume — this takes a few seconds…'}
            {dlState === 'done'    && 'Your resume downloaded successfully!'}
            {dlState === 'idle'    && 'Preparing your download…'}
            {dlState === 'error'   && 'Download encountered an issue — see below.'}
          </p>

          <ScoreBadge score={atsScore} />

          {paymentId && (
            <div style={{
              marginTop: 16, padding: '7px 14px', borderRadius: 8,
              background: 'var(--rb-cream)', border: '1px solid var(--rb-border)',
              fontSize: '0.7rem', color: 'var(--rb-muted)', fontFamily: "'DM Mono', monospace",
            }}>
              Payment ID: {paymentId}
            </div>
          )}

          {dlState === 'error' && (
            <div style={{
              marginTop: 16, padding: '12px 15px', borderRadius: 10,
              background: 'rgba(192,57,43,0.07)', border: '1px solid rgba(192,57,43,0.22)',
              fontSize: '0.8rem', color: '#C0392B',
              display: 'flex', alignItems: 'flex-start', gap: 8, textAlign: 'left',
            }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{dlError}</span>
            </div>
          )}

          <button
            onClick={triggerDownload}
            disabled={dlState === 'loading'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              marginTop: 24, padding: '13px 20px', borderRadius: 50, border: 'none',
              width: '100%', cursor: dlState === 'loading' ? 'not-allowed' : 'pointer',
              background: 'var(--rb-ink)', color: 'var(--rb-cream)',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 700,
              opacity: dlState === 'loading' ? 0.7 : 1,
              transition: 'all 0.18s',
            }}
            onMouseEnter={e => { if (dlState !== 'loading') { e.currentTarget.style.background = 'var(--rb-gold)'; e.currentTarget.style.color = 'var(--rb-ink)'; }}}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--rb-ink)'; e.currentTarget.style.color = 'var(--rb-cream)'; }}
          >
            {dlState === 'loading'
              ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Compiling PDF…</>
              : dlState === 'done'
              ? <><Download size={16} /> Download Again</>
              : <><Download size={16} /> Download Resume</>}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'var(--rb-border)' }} />
            <span style={{ fontSize: '0.68rem', color: 'var(--rb-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              What's Next
            </span>
            <div style={{ flex: 1, height: 1, background: 'var(--rb-border)' }} />
          </div>

          <div style={{ textAlign: 'left' }}>
            {[
              { icon: <FileText size={14} />,     text: 'Tailor your resume for each role by tweaking keywords.' },
              { icon: <Star size={14} />,          text: 'Run the ATS check again after any major edits.' },
              { icon: <ExternalLink size={14} />,  text: 'Upload to LinkedIn, Naukri, or your target company portal.' },
            ].map(({ icon, text }, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'var(--rb-gold-bg)', border: '1px solid rgba(200,151,58,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--rb-gold)',
                }}>{icon}</div>
                <span style={{ fontSize: '0.8rem', color: 'var(--rb-muted)', lineHeight: 1.55, paddingTop: 6 }}>{text}</span>
              </div>
            ))}
          </div>

          <button
            onClick={onReset}
            style={{
              marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              width: '100%', padding: '10px 16px', borderRadius: 50,
              border: '1px solid var(--rb-border-md)', background: 'transparent',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', fontWeight: 600,
              color: 'var(--rb-muted)', cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(26,24,20,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <RefreshCw size={13} /> Build Another Resume
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PaymentSuccess;